import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { Moment } from 'moment';

import {
  MailComposeDefaultsFetchDocument,
  MailComposeDefaultsFetchQuery,
  MailComposeDefaultsFetchQueryVariables,
  MailComposeInput,
  MailDraftFieldsFragment,
  MailDraftViewDocument,
  MailDraftViewQuery,
  MailDraftViewQueryVariables,
  MailSendMode,
  useDiscardMailDraftMutation,
  useSendMailMessageMutation,
  useStoreMailDraftMutation,
  useSuggestMailAiReplyMutation,
} from 'core/graphql';

import { IMailAddress, IMailResult, mailFailure } from './mailModel';
import { MailToneKey, toneEnum } from './mailAiModel';

export type IMailDraft = MailDraftFieldsFragment;
export type MailComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

const MODE_ENUM: Record<MailComposeMode, MailSendMode> = {
  new: MailSendMode.New,
  reply: MailSendMode.Reply,
  replyAll: MailSendMode.ReplyAll,
  forward: MailSendMode.Forward,
};

export const mailModeEnum = (mode: MailComposeMode): MailSendMode =>
  MODE_ENUM[mode];

/** Что набрано в редакторе — в форме, близкой к `MailComposeInput`. */
export interface IMailComposeValues {
  mailboxId: string;
  mode: MailComposeMode;
  replyToMessageId?: string | null;
  to: IMailAddress[];
  cc: IMailAddress[];
  bcc: IMailAddress[];
  subject: string;
  html: string;
  text: string;
  attachmentIds: string[];
  /** Пересылка: вложения исходного письма. */
  includeOriginalAttachments?: boolean;
  /** Черновик Gmail (id письма-черновика), если уже сохранён. */
  draftId?: string | null;
  /**
   * Вложения сохранённого черновика, которые остаются (индексы); `null` —
   * все. Сервер переносит их в обновлённый черновик / письмо.
   */
  keepAttachmentIndexes?: number[] | null;
  /** ТЗ 0122, «ИИ в почте»: вставленный черновик ИИ — для учёта качества. */
  aiSuggestionId?: string | null;
}

const addresses = (list: IMailAddress[]) =>
  list.map(({ email, name }) => ({ email, name: name || null }));

export const buildComposeInput = (
  values: IMailComposeValues,
): MailComposeInput => ({
  mailboxId: values.mailboxId,
  mode: mailModeEnum(values.mode),
  replyToMessageId: values.replyToMessageId || null,
  to: addresses(values.to),
  cc: addresses(values.cc),
  bcc: addresses(values.bcc),
  subject: values.subject,
  html: values.html,
  text: values.text,
  attachmentIds: values.attachmentIds,
  includeOriginalAttachments: values.includeOriginalAttachments ?? null,
  draftId: values.draftId || null,
  keepAttachmentIndexes: values.draftId
    ? values.keepAttachmentIndexes ?? null
    : null,
  aiSuggestionId: values.aiSuggestionId || null,
});

export interface IMailSendResult extends IMailResult {
  threadId?: string | null;
  messageId?: string | null;
  scheduledId?: string | null;
}

export interface IMailDraftResult extends IMailResult {
  draft?: IMailDraft | null;
}

/**
 * ТЗ 0122 — редактор письма: черновик Gmail, отправка (сейчас / позже),
 * адресаты ответа по правилам сервера, ИИ-ответ и «что делать».
 */
export const useMailCompose = () => {
  const client = useApolloClient();
  const [saveMut] = useStoreMailDraftMutation();
  const [deleteMut] = useDiscardMailDraftMutation();
  const [sendMut] = useSendMailMessageMutation();
  const [aiReplyMut] = useSuggestMailAiReplyMutation();

  const saveDraft = useCallback(
    async (values: IMailComposeValues): Promise<IMailDraftResult> => {
      try {
        const { data } = await saveMut({
          variables: { input: buildComposeInput(values) },
        });
        const result = data?.mailSaveDraft;
        if (!result) return { ok: false, errorCode: 'UNAVAILABLE' };
        return {
          ok: result.ok,
          error: result.error,
          errorCode: result.errorCode,
          draft: result.draft,
        };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [saveMut],
  );

  const deleteDraft = useCallback(
    async (draftId: string): Promise<IMailResult> => {
      try {
        const { data } = await deleteMut({ variables: { draftId } });
        const result = data?.mailDeleteDraft;
        return result
          ? { ok: result.ok, error: result.error, errorCode: result.errorCode }
          : { ok: false, errorCode: 'UNAVAILABLE' };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [deleteMut],
  );

  const send = useCallback(
    async (
      values: IMailComposeValues,
      sendAt: Moment | null = null,
    ): Promise<IMailSendResult> => {
      try {
        const { data } = await sendMut({
          variables: {
            input: buildComposeInput(values),
            sendAt: sendAt ? sendAt.toISOString() : null,
          },
        });
        const result = data?.mailSend;
        if (!result) return { ok: false, errorCode: 'UNAVAILABLE' };
        return {
          ok: result.ok,
          error: result.error,
          errorCode: result.errorCode,
          threadId: result.threadId,
          messageId: result.messageId,
          scheduledId: result.scheduledId,
        };
      } catch (caught) {
        return mailFailure(caught);
      }
    },
    [sendMut],
  );

  /** Адресаты и тема ответа / пересылки — по правилам сервера. */
  const composeDefaults = useCallback(
    async (messageId: string, mode: MailComposeMode) => {
      const { data } = await client.query<
        MailComposeDefaultsFetchQuery,
        MailComposeDefaultsFetchQueryVariables
      >({
        query: MailComposeDefaultsFetchDocument,
        variables: { messageId, mode: mailModeEnum(mode) },
        fetchPolicy: 'no-cache',
      });
      return data.mailComposeDefaults;
    },
    [client],
  );

  /** Сохранённый черновик целиком (тело грузит сервер). */
  const loadDraft = useCallback(
    async (id: string): Promise<IMailDraft | null> => {
      const { data } = await client.query<
        MailDraftViewQuery,
        MailDraftViewQueryVariables
      >({
        query: MailDraftViewDocument,
        variables: { id },
        fetchPolicy: 'no-cache',
      });
      return data.mailDraft ?? null;
    },
    [client],
  );

  const aiReply = useCallback(
    async (
      threadId: string,
      instruction: string,
      tone: MailToneKey = 'default',
    ) => {
      try {
        const { data } = await aiReplyMut({
          variables: {
            threadId,
            instruction: instruction.trim() || null,
            tone: toneEnum(tone),
          },
        });
        const result = data?.mailAiReply;
        if (!result) {
          return { ok: false, errorCode: 'UNAVAILABLE', reply: null };
        }
        return {
          ok: result.ok,
          error: result.error,
          errorCode: result.errorCode,
          reply: result.reply ?? null,
        };
      } catch (caught) {
        return { ...mailFailure(caught), reply: null };
      }
    },
    [aiReplyMut],
  );

  return {
    saveDraft,
    deleteDraft,
    send,
    composeDefaults,
    loadDraft,
    aiReply,
  };
};

export type IMailCompose = ReturnType<typeof useMailCompose>;
